import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "components/Button";
import Checkbox from "components/CheckBox/Checkbox";
import { IconCheck } from "icons";
import { PackageUpgradeModalProps } from "./PackageUpgradeModal.type";

const noop = () => undefined;

const PackageUpgradeModal: React.FC<PackageUpgradeModalProps> = ({ show, onClose, scenario }) => {
  const { t } = useTranslation();
  const { oldPlan, newPlan, features, yearlyBreakdown } = scenario;
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (show) {
      setAcknowledged(false);
    }
  }, [show]);

  return (
    <Modal show={show} onClose={noop} backdropDisabled bodyClassName="bg-black-100 !max-w-[540px] !w-full" autoFocus={false}>
      <div className="flex flex-col max-h-[700px]">
        {/* Scrollable content */}
        <div className="flex flex-col gap-6 p-6 overflow-y-auto">
          {/* Title */}
          <h2 className="text-h2 text-white">{t("billing:package_upgrade.title")}</h2>

          {/* Plan Change Card */}
          <div className="flex flex-col gap-3 p-4 rounded-lg bg-black-200 border border-black-300">
            <span className="text-bodySm text-grey uppercase tracking-wider">{t("billing:package_upgrade.plan_change_label")}</span>

            <div className="flex items-center gap-4">
              {/* Old Plan */}
              <div className="flex flex-col flex-1">
                <span className="text-bodySm text-grey">{t("billing:package_upgrade.from")}</span>
                <span className="text-h6 text-grey line-through">{oldPlan.name}</span>
                <span className="text-bodySm text-grey">{oldPlan.price}</span>
              </div>

              {/* Arrow */}
              <span className="text-h4 text-grey">&#8594;</span>

              {/* New Plan */}
              <div className="flex flex-col flex-1">
                <span className="text-bodySm text-purpleGreen">{t("billing:package_upgrade.to")}</span>
                <span className="text-h6 text-white">{newPlan.name}</span>
                <span className="text-h6 text-purpleGreen">{newPlan.price}</span>
              </div>
            </div>

            {/* Yearly Discount Breakdown */}
            {yearlyBreakdown && (
              <div className="flex flex-col gap-1.5 mt-2 pt-3 border-t border-black-300">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-purpleGreen/10 text-purpleGreen text-bodySm font-semibold">
                    {t("billing:package_upgrade.yearly_discount_badge", { percentage: yearlyBreakdown.discountPercentage })}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 text-bodySm text-grey">
                  <span>
                    <Trans
                      i18nKey="billing:package_upgrade.yearly_breakdown"
                      values={{ full: yearlyBreakdown.fullPrice, final: yearlyBreakdown.finalPrice, equivalent: yearlyBreakdown.monthlyEquivalent }}
                      components={{ bold: <span className="text-white font-semibold" /> }}
                    />
                  </span>
                  <span className="text-purpleGreen">{yearlyBreakdown.savingsNote}</span>
                </div>
              </div>
            )}
          </div>

          {/* Features */}
          <div className="flex flex-col gap-3">
            <span className="text-h6 text-white">{t("billing:package_upgrade.whats_new", { plan: newPlan.name.replace(" (Yearly)", "") })}</span>
            <ul className="flex flex-col gap-3">
              {features.map((feature) => (
                <li key={feature.label} className="flex gap-2.5">
                  <IconCheck className="w-5 h-5 text-purpleGreen shrink-0 mt-0.5" />
                  <div className="flex flex-col">
                    <span className="text-h7 text-white">{feature.label}</span>
                    <span className="text-bodySm text-grey">{feature.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Notes */}
          <div className="flex flex-col gap-2 text-bodySm text-grey">
            <p>{t("billing:package_upgrade.renewal_note")}</p>
            <p>{t("billing:package_upgrade.cancel_note")}</p>
          </div>
        </div>

        {/* Footer - always visible */}
        <div className="flex flex-col gap-4 p-6 border-t border-black-300">
          <Checkbox checked={acknowledged} checkedColor="bg-purpleGreen" boxSize="lg" containerClassName="flex items-center gap-3 select-none" onChange={(_, checked) => setAcknowledged(checked)}>
            <span className="text-bodySm text-grey">{t("billing:package_upgrade.acknowledge")}</span>
          </Checkbox>

          <Button className="btn-primary w-full" onClick={onClose} disabled={!acknowledged}>
            {t("billing:package_upgrade.got_it")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PackageUpgradeModal;
